import { Grid, Stack } from "@mui/material";
import { useCallback, useEffect } from "react";
import { Outlet } from "react-router";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import biApi from "../../../../api/biApi";
import useClientContext from "../../../../context/useClientContext";
import { BiApiClientProvider } from "../sv/contexts/BiApiClientProvider.tsx";
import type { BiApiClientBase } from "../sv/contexts/BiApiClientProviderContext.ts";
import ReportsManager from "./contexts/reports-manager/ReportsManager";
import { ReportsContextProvider } from "./contexts/reports/ReportsContextProvider";
import { GroupsContextProvider } from "./groups/contexts/GroupsContextProvider.tsx";
import useGroups from "./groups/hooks/useGroups.ts";
import useReports from "./hooks/useReports";
import { getEditApiReportUrl, getViewApiReportUrl } from "./reports/utilities/editReportUrl";

const CompanyApiReportsPage = () => {
  const { clientCode } = useClientContext();
  const loadReports = useCallback((signal?: AbortSignal) => biApi.apiReports.getReports({}, signal), []);
  const { reports, ui: reportsUi, actions: reportsActions } = useReports(loadReports, "Api");
  const {
    groups,
    actions: groupActions,
    ui: groupsUi,
  } = useGroups({
    ...biApi.apiReportGroup,
    applyGroupSharing: reportsActions.applyGroupSharing,
  });

  useEffect(() => {
    groupActions.load(clientCode);
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [clientCode, groupActions.load]);

  if (!reportsUi.loaded || !groupsUi.loaded) {
    return (
      <Stack sx={{ height: "100%" }}>
        <InlineLoader text="Loading reports..." />
      </Stack>
    );
  }

  if (reportsUi.error !== undefined || groupsUi.error !== undefined) {
    return (
      <Stack sx={{ height: "100%" }}>
        <DataLoadingFailed />
      </Stack>
    );
  }

  return (
    <Grid container sx={{ flex: 1, height: "100%", width: "100%" }}>
      <BiApiClientProvider<BiApiClientBase>
        api={{
          validateName: biApi.apiReports.validateReportName,
          getReportSharing: biApi.apiReports.getReportSharing,
          updateReportSharing: biApi.apiReports.updateReportSharing,
          getEditReportUrl: getEditApiReportUrl,
          getViewReportUrl: getViewApiReportUrl,
        }}
      >
        <ReportsContextProvider reports={reports} ui={reportsUi} actions={reportsActions}>
          <GroupsContextProvider
            groups={groups}
            ui={groupsUi}
            actions={{ ...groupActions, reloadReports: reportsActions.reloadReports }}
          >
            <ReportsManager reports={reports} reportUsage={"Api"}>
              <Outlet />
            </ReportsManager>
          </GroupsContextProvider>
        </ReportsContextProvider>
      </BiApiClientProvider>
    </Grid>
  );
};

export default CompanyApiReportsPage;
